"use client";

import { useAuth, useOrganization, useUser } from "@clerk/nextjs";
import { useMutation, useQuery } from "convex/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "@/convex/_generated/api";
import type { Doc } from "@/convex/_generated/dataModel";
import type { Workspace } from "@/lib/llamox";
import { LlamoxClient } from "@/lib/llamox";
import {
  appendSlugSuffix,
  isSlugConflict,
  mergeWithOverlay,
  readStoredId,
  slugify,
  writeStoredId,
} from "@/lib/llamox/workspace";

const MAX_WORKSPACE_CREATE_ATTEMPTS = 3;
const HEARTBEAT_INTERVAL_MS = 60_000;

type WorkspaceDoc = Doc<"workspaces"> & {
  membership?: { role: string; joinedAt?: number; lastActive?: number };
};
type ActivityDoc = Doc<"workspaceActivity">;
type MemberDoc = Doc<"workspaceMembers">;

interface WorkspaceContextValue {
  client: LlamoxClient;
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  setActiveWorkspaceId: (id: string) => void;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  createWorkspace: (input: {
    name: string;
    slug?: string;
  }) => Promise<Workspace>;
  /** Recent workspace activity from the Convex live subscription. Empty in REST-only mode. */
  activity: ActivityDoc[];
  /** Workspace members from the Convex live subscription. Empty in REST-only mode. */
  members: MemberDoc[];
  /** True when a Convex client is mounted (i.e. NEXT_PUBLIC_CONVEX_URL set). */
  hasConvex: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({
  hasConvex,
  children,
}: {
  hasConvex: boolean;
  children: React.ReactNode;
}) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  // Scope the active-workspace memory per Clerk org so switching orgs
  // doesn't leak the previously-active workspace across tenants. The
  // backend list is JWT-derived (same across orgs), but the *remembered*
  // selection should follow the org switch.
  const workspaceScope = organization?.id ?? "personal";

  const client = useMemo(
    () => new LlamoxClient(async () => (isSignedIn ? await getToken() : null)),
    [getToken, isSignedIn],
  );

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedWorkspaces, setHasLoadedWorkspaces] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [liveOverlay, setLiveOverlay] = useState<WorkspaceDoc | null>(null);
  const [liveActivity, setLiveActivity] = useState<ActivityDoc[]>([]);
  const [liveMembers, setLiveMembers] = useState<MemberDoc[]>([]);
  const bootstrapTriedRef = useRef(false);

  const setActiveWorkspaceId = useCallback(
    (id: string) => {
      setActiveId(id);
      writeStoredId(id, workspaceScope);
    },
    [workspaceScope],
  );

  const refresh = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    setIsLoading(true);
    setHasLoadedWorkspaces(false);
    setError(null);
    try {
      const data = await client.listWorkspaces();
      const list = Array.isArray(data) ? data : data?.workspaces || [];
      setWorkspaces(list);

      const stored = readStoredId(workspaceScope);
      const storedExists = list.some((w) => w.id === stored);

      if (storedExists) {
        setActiveId(stored);
      } else if (list.length > 0) {
        setActiveId(list[0].id);
        writeStoredId(list[0].id, workspaceScope);
      } else {
        setActiveId("");
        writeStoredId("", workspaceScope);
      }
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setHasLoadedWorkspaces(true);
      setIsLoading(false);
    }
  }, [client, isLoaded, isSignedIn, workspaceScope]);

  const createWorkspace = useCallback(
    async (input: { name: string; slug?: string }) => {
      const baseSlug = input.slug ? slugify(input.slug) : slugify(input.name);
      if (!baseSlug) throw new Error("Workspace name produces an invalid slug");

      const suffixSeed =
        user?.id ||
        user?.primaryEmailAddress?.emailAddress ||
        Math.random().toString(36).slice(2, 10);
      const candidates = [
        baseSlug,
        appendSlugSuffix(baseSlug, suffixSeed),
        appendSlugSuffix(baseSlug, `${suffixSeed}-${Date.now().toString(36)}`),
      ].filter((slug, index, all) => all.indexOf(slug) === index);

      let lastSlugConflict: unknown;
      for (const slug of candidates.slice(0, MAX_WORKSPACE_CREATE_ATTEMPTS)) {
        try {
          const res = await client.createWorkspace({
            name: input.name,
            slug,
            ...(organization?.id ? { clerkOrgId: organization.id } : {}),
          });
          const ws =
            (res as { workspace?: Workspace }).workspace ??
            (res as unknown as Workspace);
          await refresh();
          if (ws?.id) setActiveWorkspaceId(ws.id);
          return ws;
        } catch (e) {
          if (!isSlugConflict(e)) throw e;
          lastSlugConflict = e;
        }
      }

      throw lastSlugConflict instanceof Error
        ? lastSlugConflict
        : new Error("Workspace slug is already taken");
    },
    [
      client,
      organization?.id,
      refresh,
      setActiveWorkspaceId,
      user?.id,
      user?.primaryEmailAddress?.emailAddress,
    ],
  );

  // Initial fetch when the user signs in.
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setWorkspaces([]);
      setActiveId("");
      setHasLoadedWorkspaces(false);
      writeStoredId("", workspaceScope);
      bootstrapTriedRef.current = false;
      return;
    }
    refresh();
  }, [isLoaded, isSignedIn, refresh, workspaceScope]);

  useEffect(() => {
    bootstrapTriedRef.current = false;
    setHasLoadedWorkspaces(false);
  }, [workspaceScope]);

  // Bootstrap the first workspace in the active Clerk organization scope.
  // Drives off the REST list (not the live overlay) so a brief Convex
  // `[]` while the worker mirror is still propagating doesn't cause us to
  // create a duplicate workspace.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || isLoading) return;
    if (!hasLoadedWorkspaces) return;
    if (error) return;
    if (workspaces.length > 0) return;
    if (bootstrapTriedRef.current) return;
    if (!user) return;

    bootstrapTriedRef.current = true;
    const fallbackName =
      user.fullName ||
      user.username ||
      user.primaryEmailAddress?.emailAddress?.split("@")[0] ||
      "My workspace";

    void (async () => {
      try {
        await createWorkspace({ name: fallbackName });
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    })();
  }, [
    isLoaded,
    isSignedIn,
    isLoading,
    hasLoadedWorkspaces,
    error,
    workspaces.length,
    user,
    createWorkspace,
  ]);

  const restActiveWorkspace = useMemo(
    () => workspaces.find((w) => w.id === activeId) || null,
    [workspaces, activeId],
  );

  const activeWorkspace = useMemo(
    () =>
      mergeWithOverlay(
        restActiveWorkspace,
        liveOverlay && liveOverlay.workspaceId === activeId
          ? liveOverlay
          : null,
      ),
    [restActiveWorkspace, liveOverlay, activeId],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      client,
      workspaces,
      activeWorkspace,
      setActiveWorkspaceId,
      isLoading,
      error,
      refresh,
      createWorkspace,
      activity: liveActivity,
      members: liveMembers,
      hasConvex,
    }),
    [
      client,
      workspaces,
      activeWorkspace,
      setActiveWorkspaceId,
      isLoading,
      error,
      refresh,
      createWorkspace,
      liveActivity,
      liveMembers,
      hasConvex,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {hasConvex && (
        <ConvexLiveSync
          activeWorkspaceId={activeId}
          onOverlayChange={setLiveOverlay}
          onActivityChange={setLiveActivity}
          onMembersChange={setLiveMembers}
        />
      )}
      {children}
    </WorkspaceContext.Provider>
  );
}

/**
 * Hidden child that lives inside both ConvexProviderWithClerk and
 * WorkspaceProvider. It owns the Convex `useQuery`/`useMutation` calls and
 * pushes results back up via setters. Mounted only when Convex is configured;
 * the WorkspaceProvider's state stays at its empty defaults otherwise so
 * no consumer crashes in REST-only mode.
 */
function ConvexLiveSync({
  activeWorkspaceId,
  onOverlayChange,
  onActivityChange,
  onMembersChange,
}: {
  activeWorkspaceId: string;
  onOverlayChange: (v: WorkspaceDoc | null) => void;
  onActivityChange: (v: ActivityDoc[]) => void;
  onMembersChange: (v: MemberDoc[]) => void;
}) {
  const overlay = useQuery(
    api.workspaces.getByWorkspaceId,
    activeWorkspaceId ? { workspaceId: activeWorkspaceId } : "skip",
  );
  const activity = useQuery(
    api.workspaces.getActivity,
    activeWorkspaceId ? { workspaceId: activeWorkspaceId, limit: 50 } : "skip",
  );
  const members = useQuery(
    api.workspaces.getMembers,
    activeWorkspaceId ? { workspaceId: activeWorkspaceId } : "skip",
  );
  const heartbeat = useMutation(api.workspaces.updateActivity);

  useEffect(() => {
    if (overlay === undefined) return;
    onOverlayChange((overlay as WorkspaceDoc | null) ?? null);
  }, [overlay, onOverlayChange]);

  useEffect(() => {
    if (activity === undefined) return;
    onActivityChange(activity as ActivityDoc[]);
  }, [activity, onActivityChange]);

  useEffect(() => {
    if (members === undefined) return;
    onMembersChange(members as MemberDoc[]);
  }, [members, onMembersChange]);

  // Reset overlay when the active workspace changes so a new selection
  // doesn't briefly inherit the previous workspace's plan/name.
  useEffect(() => {
    if (!activeWorkspaceId) {
      onOverlayChange(null);
      onActivityChange([]);
      onMembersChange([]);
    }
  }, [activeWorkspaceId, onOverlayChange, onActivityChange, onMembersChange]);

  // Heartbeat: keep `lastActivity` warm on the active workspace. The mutation
  // is a no-op for deleting workspaces server-side, so it's safe to fire
  // unconditionally.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      void heartbeat({ workspaceId: activeWorkspaceId }).catch(() => {
        /* swallow — heartbeats are best-effort */
      });
    };
    tick();
    const id = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [activeWorkspaceId, heartbeat]);

  return null;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return ctx;
}

export function useLlamoxClient(): LlamoxClient {
  return useWorkspace().client;
}

/** Recent workspace activity, live when Convex is configured. */
export function useWorkspaceActivity(): ActivityDoc[] {
  return useWorkspace().activity;
}

/** Workspace members, live when Convex is configured. */
export function useWorkspaceMembers(): MemberDoc[] {
  return useWorkspace().members;
}
